
/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Check, Close, ArrowBack } from '@mui/icons-material';
import { Layout } from '../LandingLayout';

import { getPlanData } from '../service/planService';

export const ScrollAnimation = ({ children, sx, direction, delay, ...props }) => (
  <Box
    sx={{
      ...sx,
      animation: 'fadeInUp 0.6s ease-out forwards',
      opacity: 0,
      transform: 'translateY(20px)',
      animationDelay: `${delay || 0}ms`,
      '@keyframes fadeInUp': {
        to: {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
    }}
    {...props}
  >
    {children}
  </Box>
);

export const PricingComparisonTable = () => {
  const allPlans = getPlanData();
  const allFeaturesSet = new Set();
  const planMaps = {};
  const navigate = useNavigate();

  // 1. Establish the "Master Order" by parsing the Enterprise plan first.
  // This ensures the table rows match the logical groupings we set in the data.
  const enterprisePlan = allPlans.find((p) => p.id === 'enterprise');
  if (enterprisePlan) {
    const extractAndAddFeature = (f) => {
      const idx = f.indexOf(':');
      allFeaturesSet.add(idx !== -1 ? f.substring(0, idx).trim() : f.trim());
    };
    (enterprisePlan.includedFeatures || []).forEach(extractAndAddFeature);
    (enterprisePlan.excludedFeatures || []).forEach(extractAndAddFeature);
  }

  // 2. Parse all plans to build the comparison map
  allPlans.forEach((plan) => {
    const map = {};
    (plan.includedFeatures || []).forEach((f) => {
      const firstColonIdx = f.indexOf(':');
      if (firstColonIdx !== -1) {
        const key = f.substring(0, firstColonIdx).trim();
        const val = f.substring(firstColonIdx + 1).trim();
        map[key] = val;
        allFeaturesSet.add(key); // Catch any stragglers not in Enterprise
      } else {
        map[f.trim()] = true;
        allFeaturesSet.add(f.trim());
      }
    });
    (plan.excludedFeatures || []).forEach((f) => {
      map[f.trim()] = false;
      allFeaturesSet.add(f.trim());
    });
    planMaps[plan.id] = map;
  });

  const uniqueFeatures = Array.from(allFeaturesSet);

  return (
    <ScrollAnimation direction="up">
      <Box sx={{ mb: 4, px: { xs: 2, md: 4 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() =>
            navigate('/', {
              state: {
                scrollTo: 'pricing',
              },
            })
          }
          sx={{ mb: 1, mt: 2, fontWeight: 700 }}
          color="primary"
        >
          Back to Plans
        </Button>

        <Typography variant="h4" fontWeight={800} mb={1}>
          Compare Features
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Find the perfect features and allocations that fit your organizational scale.
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: '25%', color: 'text.secondary' }}>
                  Features
                </TableCell>
                {allPlans.map((plan) => (
                  <TableCell key={plan.id} align="center" sx={{ minWidth: 160, width: '18.75%' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: `${plan.accentColor}15`,
                          color: plan.accentColor,
                        }}
                      >
                        {plan.icon}
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {plan.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {uniqueFeatures.map((feature) => (
                <TableRow
                  key={feature}
                  sx={{
                    '&:last-child td, &:last-child th': { borderBottom: 0 }, // <-- Only remove the bottom border
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}
                  >
                    {feature}
                  </TableCell>

                  {allPlans.map((plan) => {
                    const val = planMaps[plan.id][feature];

                    return (
                      <TableCell key={`${plan.id}-${feature}`} align="center">
                        {val === true ? (
                          <Check sx={{ color: plan.accentColor, fontSize: 22 }} />
                        ) : val === false || val === undefined ? (
                          <Close sx={{ color: 'divider', fontSize: 22 }} />
                        ) : (
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontSize: '0.8rem',
                              color: 'text.secondary',
                              maxWidth: 180, // Prevents long strings (like roles) from blowing up column widths
                              mx: 'auto',
                              lineHeight: 1.4,
                            }}
                          >
                            {val}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ScrollAnimation>
  );
};

const PricingComparisonPage = () => (
  <Layout isMinimal hideCTA>
    <Box
      sx={{
        pt: 10, // spacing below fixed navbar
        pb: 6,
        bgcolor: 'background.default',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="xl">
        <PricingComparisonTable />
      </Container>
    </Box>
  </Layout>
);

export default PricingComparisonPage;
